import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, Badge } from '@medikei/ui';
import { Button } from '@/components/ui/Button';
import { Input } from '@medikei/ui';
import {
  DollarSign,
  TrendingUp,
  ArrowUpRight,
  PieChart as PieChartIcon,
  RefreshCw,
  Search,
  Users,
  CheckCircle2,
  Clock,
  Percent,
  Receipt,
  FileText,
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
} from 'recharts';
import { httpClient } from '@/api';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { ErrorRetry } from '@/components/ui/ErrorRetry';
import { EmptyState } from '@/components/ui/empty-state';
import { cn } from '@/utils/cn';

export interface FinancialOverviewResponse {
  totalRevenue: number;
  totalPayments: number;
  pendingPayouts: number;
  completedPayouts: number;
  totalCommissions: number;
  totalDoctorPayouts: number;
  paidDoctorsCount: number;
  averageTicket: number;
  platformMarginPercent: number;
  netRevenue: number;
  totalRefunds: number;
  refundAmount: number;
  totalChargebacks: number;
  chargebackAmount: number;
}

export interface FinancialAnalyticsResponse {
  timeline: Array<{
    date: string;
    revenue: number;
    platformFee: number;
    doctorAmount: number;
    count: number;
  }>;
  byModality: Array<{
    modality: string;
    label: string;
    count: number;
    revenue: number;
    percentage: number;
  }>;
  byPaymentMethod: Array<{
    method: string;
    label: string;
    count: number;
    revenue: number;
    percentage: number;
  }>;
  topDoctors: Array<{
    doctorId: string;
    doctorName: string;
    consultations: number;
    revenue: number;
    doctorAmount: number;
  }>;
}

export interface PaymentItem {
  id: string;
  amount: number;
  status: string;
  method: string;
  patientName: string;
  patientEmail: string | null;
  doctorName: string;
  specialty: string | null;
  serviceType: string;
  appointmentId: string;
  createdAt: string;
}

export interface PaymentSplitItem {
  id: string;
  paymentId: string;
  appointmentId: string;
  doctorName: string;
  patientName: string;
  patientEmail: string | null;
  specialty: string | null;
  serviceType: string;
  grossAmount: number;
  gatewayFee: number;
  netAmount: number;
  platformFee: number;
  doctorAmount: number;
  commissionPercent: number;
  doctorPercent: number;
  status: string;
  createdAt: string;
}

type PeriodType = 'today' | 'week' | 'month' | 'year' | 'all';
type FinancialTab = 'transactions' | 'splits';

const MODALITY_COLORS: Record<string, string> = {
  TELEMEDICINA: '#0ea5e9', // Azul ciano
  PRESENCIAL: '#3b82f6', // Azul royal
  DOMICILIAR: '#10b981', // Verde esmeralda
  EXPRESS: '#f59e0b', // Âmbar
};

export function AdminFinancialPage() {
  const [period, setPeriod] = useState<PeriodType>('month');
  const [activeTab, setActiveTab] = useState<FinancialTab>('transactions');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [methodFilter, setMethodFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // 1. Dados de Visão Geral (KPIs)
  const {
    data: overview,
    isLoading: overviewLoading,
    isError: overviewError,
    refetch: refetchOverview,
    isFetching: isFetchingOverview,
  } = useQuery<FinancialOverviewResponse>({
    queryKey: ['admin', 'financial', 'overview', period],
    queryFn: async () => {
      return httpClient.get<FinancialOverviewResponse>(`/admin/financial/overview?period=${period}`);
    },
  });

  // 2. Dados de Business Intelligence (Gráficos)
  const {
    data: analytics,
    isError: analyticsError,
    refetch: refetchAnalytics,
  } = useQuery<FinancialAnalyticsResponse>({
    queryKey: ['admin', 'financial', 'analytics', period],
    queryFn: async () => {
      return httpClient.get<FinancialAnalyticsResponse>(`/admin/financial/analytics?period=${period}`);
    },
  });

  // 3. Lista de Pagamentos / Transações
  const {
    data: paymentsData,
    isLoading: paymentsLoading,
    isError: paymentsError,
    refetch: refetchPayments,
  } = useQuery<{ data: PaymentItem[]; meta: any }>({
    queryKey: ['admin', 'financial', 'payments', period, statusFilter, methodFilter, searchQuery],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: '1',
        limit: '50',
        period,
      });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (methodFilter !== 'all') params.set('method', methodFilter);
      if (searchQuery.trim()) params.set('search', searchQuery.trim());

      return httpClient.get<{ data: PaymentItem[]; meta: any }>(`/admin/financial/payments?${params.toString()}`);
    },
  });

  // 4. Lista de Splits / Decomposição Financeira
  const {
    data: splitsData,
    isLoading: splitsLoading,
    isError: splitsError,
    refetch: refetchSplits,
  } = useQuery<{ data: PaymentSplitItem[]; meta: any }>({
    queryKey: ['admin', 'financial', 'splits', period, searchQuery],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: '1',
        limit: '50',
        period,
      });
      if (searchQuery.trim()) params.set('search', searchQuery.trim());

      return httpClient.get<{ data: PaymentSplitItem[]; meta: any }>(`/admin/financial/payment-splits?${params.toString()}`);
    },
  });

  const handleRefreshAll = () => {
    refetchOverview();
    refetchAnalytics();
    refetchPayments();
    refetchSplits();
  };

  const formatCurrency = (val?: number | null) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(val) || 0);
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(dateStr));
    } catch {
      return dateStr;
    }
  };

  const payments = paymentsData?.data || [];
  const splits = splitsData?.data || [];

  // Mapeamento de Timeline formatada para Recharts
  const formattedTimeline = useMemo(() => {
    if (!analytics?.timeline || analytics.timeline.length === 0) return [];
    return analytics.timeline.map((item) => {
      let label = item.date;
      if (item.date.includes('-')) {
        const parts = item.date.split('-');
        if (parts.length === 3) {
          label = `${parts[2]}/${parts[1]}`;
        } else if (parts.length === 2) {
          label = `${parts[1]}/${parts[0]}`;
        }
      }
      return {
        ...item,
        formattedDate: label,
      };
    });
  }, [analytics?.timeline]);

  const hasError = overviewError || analyticsError || paymentsError || splitsError;

  if (overviewLoading && !overview) {
    return <LoadingOverlay message="Carregando inteligência financeira e B.I..." />;
  }

  if (hasError && !overview) {
    return <ErrorRetry message="Erro ao carregar dados financeiros da plataforma" onRetry={handleRefreshAll} />;
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-7xl mx-auto text-foreground">
      {/* Header Executivo & Seletor de Período */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-2xl bg-primary/10 text-primary shrink-0 border border-primary/20 shadow-xs">
            <DollarSign className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2">
              <span>Gestão Financeira &amp; B.I.</span>
              <span className="text-[11px] font-extrabold px-2.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20 uppercase tracking-wide">
                Tempo Real
              </span>
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
              Faturamento consolidado, retenção líquida da plataforma e auditoria de repasses médicos
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2.5 flex-wrap">
          {/* Seletor de Período */}
          <div className="inline-flex rounded-xl bg-muted/60 p-1 border border-border text-xs font-semibold">
            {(
              [
                { id: 'today', label: 'Hoje' },
                { id: 'week', label: 'Semana' },
                { id: 'month', label: 'Mês' },
                { id: 'year', label: 'Ano' },
                { id: 'all', label: 'Tudo' },
              ] as const
            ).map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setPeriod(opt.id)}
                className={cn(
                  'px-3 py-1.5 rounded-lg transition-all cursor-pointer',
                  period === opt.id
                    ? 'bg-card text-foreground shadow-xs font-bold'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={handleRefreshAll}
            className="rounded-xl font-bold gap-1.5 h-9 px-3"
            disabled={isFetchingOverview}
          >
            <RefreshCw className={cn('w-3.5 h-3.5', isFetchingOverview && 'animate-spin')} />
            <span className="hidden sm:inline">Atualizar</span>
          </Button>
        </div>
      </div>

      {/* 4 Cards Principais de KPIs (Com Dados Reais e Margens) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Faturamento Bruto Total */}
        <Card className="rounded-2xl border border-border shadow-xs bg-card hover:shadow-md transition-shadow">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Faturamento Bruto</span>
              <div className="p-2 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400">
                <TrendingUp className="w-4 h-4" />
              </div>
            </div>
            <div className="text-2xl sm:text-3xl font-black text-foreground tracking-tight">
              {formatCurrency(overview?.totalRevenue)}
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
              <span>{overview?.totalPayments || 0} consultas pagas</span>
              <span className="font-semibold text-primary">Ticket: {formatCurrency(overview?.averageTicket)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Card 2: Retenção Líquida da Plataforma (Comissão + Custos) */}
        <Card className="rounded-2xl border border-border shadow-xs bg-card hover:shadow-md transition-shadow">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Receita Medikei</span>
              <div className="p-2 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                <Percent className="w-4 h-4" />
              </div>
            </div>
            <div className="text-2xl sm:text-3xl font-black text-emerald-600 dark:text-emerald-400 tracking-tight">
              {formatCurrency(overview?.totalCommissions)}
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
              <span>Margem líquida</span>
              <span className="font-bold text-emerald-600 dark:text-emerald-400">
                {Number(overview?.platformMarginPercent || 0).toFixed(0)}% retido
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Card 3: Repasse Líquido aos Médicos */}
        <Card className="rounded-2xl border border-border shadow-xs bg-card hover:shadow-md transition-shadow">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Repasse aos Médicos</span>
              <div className="p-2 rounded-xl bg-purple-500/10 text-purple-600 dark:text-purple-400">
                <Users className="w-4 h-4" />
              </div>
            </div>
            <div className="text-2xl sm:text-3xl font-black text-foreground tracking-tight">
              {formatCurrency(overview?.totalDoctorPayouts)}
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
              <span>Creditado em carteiras</span>
              <span className="font-bold text-purple-600 dark:text-purple-400">
                {overview?.paidDoctorsCount || 0} médicos remunerados
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Card 4: Saques & Estornos */}
        <Card className="rounded-2xl border border-border shadow-xs bg-card hover:shadow-md transition-shadow">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Saques &amp; Estornos</span>
              <div className="p-2 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400">
                <ArrowUpRight className="w-4 h-4" />
              </div>
            </div>
            <div className="text-2xl sm:text-3xl font-black text-foreground tracking-tight">
              {formatCurrency(overview?.pendingPayouts)}
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
              <span>Saques pendentes</span>
              <span className="font-semibold text-rose-500">
                Chargebacks: {formatCurrency(overview?.chargebackAmount)}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Módulo de B.I. & Inteligência de Negócio (Gráficos) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Gráfico 1: Evolução Temporal da Receita */}
        <Card className="rounded-3xl border border-border shadow-xs bg-card lg:col-span-8 overflow-hidden">
          <CardHeader className="border-b border-border/80 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base sm:text-lg font-bold flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-primary" />
                  <span>Evolução do Faturamento &amp; Splits</span>
                </CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Comparativo entre faturamento bruto, receita Medikei e repasse aos médicos
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            {formattedTimeline.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">
                Nenhuma transação registrada no período selecionado.
              </div>
            ) : (
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={formattedTimeline} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorDoctor" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorPlatform" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" className="opacity-10" />
                    <XAxis dataKey="formattedDate" stroke="currentColor" className="opacity-50 text-[11px]" />
                    <YAxis
                      stroke="currentColor"
                      className="opacity-50 text-[11px]"
                      tickFormatter={(val) => `R$${val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val}`}
                    />
                    <Tooltip
                      formatter={(value: any) => [formatCurrency(Number(value)), '']}
                      labelFormatter={(label) => `Data: ${label}`}
                      contentStyle={{
                        backgroundColor: 'var(--card, #ffffff)',
                        borderColor: 'var(--border, #e2e8f0)',
                        borderRadius: '1rem',
                        fontSize: '0.75rem',
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: '0.75rem', paddingTop: '10px' }} />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      name="Faturamento Bruto"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="urlColorRevenue"
                    />
                    <Area
                      type="monotone"
                      dataKey="doctorAmount"
                      name="Repasse Médicos"
                      stroke="#10b981"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="urlColorDoctor"
                    />
                    <Area
                      type="monotone"
                      dataKey="platformFee"
                      name="Comissão Medikei"
                      stroke="#0ea5e9"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="urlColorPlatform"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Gráfico 2: Distribuição por Modalidade de Consulta */}
        <Card className="rounded-3xl border border-border shadow-xs bg-card lg:col-span-4 flex flex-col justify-between">
          <CardHeader className="border-b border-border/80 pb-4">
            <CardTitle className="text-base sm:text-lg font-bold flex items-center gap-2">
              <PieChartIcon className="w-4 h-4 text-emerald-600" />
              <span>Receita por Modalidade</span>
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              Participação de Telemedicina, Presencial e Domiciliar
            </p>
          </CardHeader>
          <CardContent className="pt-4 flex-1 flex flex-col justify-center">
            {(!analytics?.byModality || analytics.byModality.length === 0) ? (
              <div className="py-12 text-center text-xs text-muted-foreground">
                Sem consultas no período
              </div>
            ) : (
              <div className="space-y-4">
                <div className="h-44 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={analytics.byModality}
                        dataKey="count"
                        nameKey="label"
                        cx="50%"
                        cy="50%"
                        innerRadius={45}
                        outerRadius={70}
                        paddingAngle={4}
                      >
                        {analytics.byModality.map((entry) => (
                          <Cell key={entry.modality} fill={MODALITY_COLORS[entry.modality] || '#64748b'} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(val: any, _name: any, item: any) => [
                          `${val} consultas (${formatCurrency(item.payload.revenue)})`,
                          item.payload.label,
                        ]}
                      />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>

                <div className="space-y-2 pt-1 border-t border-border/60">
                  {analytics.byModality.map((item) => (
                    <div key={item.modality} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <span
                          className="w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: MODALITY_COLORS[item.modality] || '#64748b' }}
                        />
                        <span className="font-medium text-foreground">{item.label}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-bold text-foreground">{formatCurrency(item.revenue)}</span>
                        <span className="text-[10px] text-muted-foreground ml-1">({item.percentage.toFixed(0)}%)</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Seção de Listagens com Abas, Filtros Avançados e Busca */}
      <Card className="rounded-3xl border border-border shadow-xs bg-card overflow-hidden">
        <CardHeader className="border-b border-border/80 pb-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            {/* Abas */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setActiveTab('transactions')}
                className={cn(
                  'px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all cursor-pointer flex items-center gap-2',
                  activeTab === 'transactions'
                    ? 'bg-primary text-primary-foreground shadow-xs'
                    : 'bg-muted/40 text-muted-foreground hover:text-foreground'
                )}
              >
                <Receipt className="w-4 h-4" />
                <span>Transações Recentes</span>
                <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-black/20 text-white">
                  {payments.length}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('splits')}
                className={cn(
                  'px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all cursor-pointer flex items-center gap-2',
                  activeTab === 'splits'
                    ? 'bg-primary text-primary-foreground shadow-xs'
                    : 'bg-muted/40 text-muted-foreground hover:text-foreground'
                )}
              >
                <FileText className="w-4 h-4" />
                <span>Auditoria de Splits</span>
                <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-black/20 text-white">
                  {splits.length}
                </span>
              </button>
            </div>

            {/* Barra de Busca e Filtros Rápidos */}
            <div className="flex items-center gap-2.5 flex-wrap">
              {activeTab === 'transactions' && (
                <>
                  {/* Filtro de Status */}
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="h-10 px-3 rounded-xl border border-border bg-card text-xs font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="all">Status: Todos</option>
                    <option value="approved">Pagos / Concluídos</option>
                    <option value="pending">Pendentes</option>
                    <option value="refunded">Estornados</option>
                  </select>

                  {/* Filtro de Método */}
                  <select
                    value={methodFilter}
                    onChange={(e) => setMethodFilter(e.target.value)}
                    className="h-10 px-3 rounded-xl border border-border bg-card text-xs font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="all">Método: Todos</option>
                    <option value="pix">PIX Instantâneo</option>
                    <option value="card">Cartão de Crédito</option>
                  </select>
                </>
              )}

              {/* Input de Busca */}
              <div className="relative min-w-[200px] flex-1 sm:flex-initial">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Buscar médico ou paciente..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-10 text-xs rounded-xl"
                />
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {/* ABA 1: TRANSAÇÕES RECENTES */}
          {activeTab === 'transactions' && (
            <div className="overflow-x-auto">
              {paymentsLoading ? (
                <div className="py-16 text-center text-sm text-muted-foreground">
                  Carregando transações...
                </div>
              ) : payments.length === 0 ? (
                <div className="py-16 text-center">
                  <EmptyState
                    icon="DollarSign"
                    title="Nenhuma transação encontrada"
                    description="Não foram encontradas transações com os filtros selecionados"
                    action={{ label: 'Recarregar', onClick: refetchPayments }}
                  />
                </div>
              ) : (
                <table className="w-full text-left text-xs">
                  <thead className="bg-muted/40 border-b border-border font-bold text-muted-foreground uppercase tracking-wider">
                    <tr>
                      <th className="px-5 py-3.5">Data / Hora</th>
                      <th className="px-5 py-3.5">Atendimento / Especialidade</th>
                      <th className="px-5 py-3.5">Paciente</th>
                      <th className="px-5 py-3.5">Médico</th>
                      <th className="px-5 py-3.5">Método</th>
                      <th className="px-5 py-3.5 text-right">Valor Pago</th>
                      <th className="px-5 py-3.5 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/60">
                    {payments.map((p) => {
                      const isApproved = p.status === 'APPROVED' || p.status === 'COMPLETED';
                      const isPending = p.status === 'PENDING';
                      return (
                        <tr key={p.id} className="hover:bg-muted/30 transition-colors">
                          <td className="px-5 py-3.5 whitespace-nowrap text-muted-foreground font-mono">
                            {formatDate(p.createdAt)}
                          </td>
                          <td className="px-5 py-3.5">
                            <span className="font-bold text-foreground block">{p.serviceType}</span>
                            <span className="text-[11px] text-muted-foreground">{p.specialty || 'Clínica Geral'}</span>
                          </td>
                          <td className="px-5 py-3.5">
                            <span className="font-semibold text-foreground block">{p.patientName}</span>
                            <span className="text-[11px] text-muted-foreground truncate max-w-[150px] block">
                              {p.patientEmail || '—'}
                            </span>
                          </td>
                          <td className="px-5 py-3.5 font-medium text-foreground">
                            {p.doctorName}
                          </td>
                          <td className="px-5 py-3.5 whitespace-nowrap">
                            <Badge variant="outline" className="text-[10px] font-bold">
                              {p.method?.includes('PIX') ? '⚡ PIX' : '💳 Cartão'}
                            </Badge>
                          </td>
                          <td className="px-5 py-3.5 text-right whitespace-nowrap font-bold text-sm text-foreground">
                            {formatCurrency(p.amount)}
                          </td>
                          <td className="px-5 py-3.5 text-center whitespace-nowrap">
                            <span
                              className={cn(
                                'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold',
                                isApproved
                                  ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20'
                                  : isPending
                                  ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20'
                                  : 'bg-rose-500/10 text-rose-700 dark:text-rose-400 border border-rose-500/20'
                              )}
                            >
                              {isApproved && <CheckCircle2 className="w-3 h-3" />}
                              {isPending && <Clock className="w-3 h-3" />}
                              <span>{isApproved ? 'Pago' : isPending ? 'Pendente' : 'Estornado'}</span>
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* ABA 2: AUDITORIA DE SPLITS FINANCEIROS */}
          {activeTab === 'splits' && (
            <div className="overflow-x-auto">
              {splitsLoading ? (
                <div className="py-16 text-center text-sm text-muted-foreground">
                  Carregando splits...
                </div>
              ) : splits.length === 0 ? (
                <div className="py-16 text-center">
                  <EmptyState
                    icon="PieChart"
                    title="Nenhum split registrado"
                    description="Não há registros de split financeiro para o filtro aplicado"
                    action={{ label: 'Recarregar', onClick: refetchSplits }}
                  />
                </div>
              ) : (
                <table className="w-full text-left text-xs">
                  <thead className="bg-muted/40 border-b border-border font-bold text-muted-foreground uppercase tracking-wider">
                    <tr>
                      <th className="px-5 py-3.5">Data / Hora</th>
                      <th className="px-5 py-3.5">Atendimento / Médico</th>
                      <th className="px-5 py-3.5 text-right">Valor Bruto</th>
                      <th className="px-5 py-3.5 text-right">Custo Direto</th>
                      <th className="px-5 py-3.5 text-right">Comissão Medikei</th>
                      <th className="px-5 py-3.5 text-right text-emerald-600 dark:text-emerald-400">Repasse Médico</th>
                      <th className="px-5 py-3.5 text-center">Margem Médica</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/60">
                    {splits.map((s) => (
                      <tr key={s.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-5 py-3.5 whitespace-nowrap text-muted-foreground font-mono">
                          {formatDate(s.createdAt)}
                        </td>
                        <td className="px-5 py-3.5">
                          <span className="font-bold text-foreground block">{s.doctorName}</span>
                          <span className="text-[11px] text-muted-foreground">{s.serviceType}</span>
                        </td>
                        <td className="px-5 py-3.5 text-right font-bold text-foreground">
                          {formatCurrency(s.grossAmount)}
                        </td>
                        <td className="px-5 py-3.5 text-right text-muted-foreground">
                          {formatCurrency(s.gatewayFee)}
                        </td>
                        <td className="px-5 py-3.5 text-right font-medium text-primary">
                          <span>{formatCurrency(s.platformFee)}</span>
                          <span className="block text-[10px] text-muted-foreground">({s.commissionPercent.toFixed(0)}%)</span>
                        </td>
                        <td className="px-5 py-3.5 text-right font-black text-sm text-emerald-600 dark:text-emerald-400">
                          {formatCurrency(s.doctorAmount)}
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          <Badge variant="outline" className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 border-emerald-500/30">
                            {s.doctorPercent.toFixed(0)}% líquido
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
